import {
  useDeleteSuperAdminClient,
  useMergeSuperAdminClients,
  useSuperAdminClient,
  useSuperAdminClients,
  useUpdateSuperAdminClient,
} from "@babackapp/private-api-client/client/use-super-admin-clients";
import type {
  ClientAccountDto,
  UpdateClientAccountBodyDto,
} from "@babackapp/private-api-client/types";
import { Button } from "@babackapp/ui/components/button";
import { Input } from "@babackapp/ui/components/input";
import { Textarea } from "@babackapp/ui/components/textarea";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AdminPageShell } from "./admin-tab-pages";
import { formatEuros } from "./clients-page";
import { MAX_LENGTH, parseAmount } from "./form-values";
import { ConfigurationSection } from "./shop-account-sections";
import {
  CLIENT_IMPORTANCE_LABELS,
  CLIENT_RELATION_LABELS,
  CLIENT_STATUS_LABELS,
} from "./shop-shared";

type FormState = {
  name: string;
  status: string;
  csm: string;
  importance: string;
  relationHealth: string;
  shopifyAgency: string;
  frontAccountId: string;
  signedAt: string;
  payPerUse: string;
  transportFee: string;
  notes: string;
};

function toForm(client: ClientAccountDto): FormState {
  return {
    name: client.name,
    status: client.status ?? "",
    csm: client.csm ?? "",
    importance: client.importance ?? "",
    relationHealth: client.relationHealth ?? "",
    shopifyAgency: client.shopifyAgency ?? "",
    frontAccountId: client.frontAccountId ?? "",
    signedAt: client.signedAt ?? "",
    payPerUse: client.payPerUse?.toString() ?? "",
    transportFee: client.transportFee?.toString() ?? "",
    notes: client.notes ?? "",
  };
}

/** Empty inputs clear the field: the server stores `null`. */
function toBody(form: FormState): UpdateClientAccountBodyDto {
  return {
    name: form.name.trim(),
    status: (form.status || null) as UpdateClientAccountBodyDto["status"],
    csm: form.csm.trim() || null,
    importance: (form.importance ||
      null) as UpdateClientAccountBodyDto["importance"],
    relationHealth: (form.relationHealth ||
      null) as UpdateClientAccountBodyDto["relationHealth"],
    shopifyAgency: form.shopifyAgency.trim() || null,
    frontAccountId: form.frontAccountId.trim() || null,
    signedAt: form.signedAt || null,
    // Checked by `isValid` first: never `undefined` here.
    payPerUse: parseAmount(form.payPerUse) ?? null,
    transportFee: parseAmount(form.transportFee) ?? null,
    notes: form.notes.trim() || null,
  };
}

/** Only what changed: the server journals, and the caches refresh, by field. */
function changedFields(
  body: UpdateClientAccountBodyDto,
  initial: UpdateClientAccountBodyDto,
): UpdateClientAccountBodyDto {
  return Object.fromEntries(
    Object.entries(body).filter(
      ([field, value]) =>
        value !== initial[field as keyof UpdateClientAccountBodyDto],
    ),
  ) as UpdateClientAccountBodyDto;
}

function SelectField({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: Record<string, { label: string }>;
  onChange: (value: string) => void;
}) {
  return (
    <label className="space-y-1">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-9 w-full rounded-md border bg-background px-2 text-sm"
      >
        <option value="">— Non défini</option>
        {Object.entries(options).map(([key, { label: optionLabel }]) => (
          <option key={key} value={key}>
            {optionLabel}
          </option>
        ))}
      </select>
    </label>
  );
}

function TextField({
  label,
  value,
  onChange,
  type = "text",
  placeholder,
  maxLength = MAX_LENGTH.short,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  placeholder?: string;
  maxLength?: number;
}) {
  return (
    <label className="space-y-1">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <Input
        type={type}
        value={value}
        placeholder={placeholder}
        maxLength={maxLength}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}

function ClientForm({ client }: { client: ClientAccountDto }) {
  const updateMutation = useUpdateSuperAdminClient();
  const [form, setForm] = useState(() => toForm(client));
  const set = (field: keyof FormState) => (value: string) =>
    setForm((previous) => ({ ...previous, [field]: value }));

  const isDirty = JSON.stringify(form) !== JSON.stringify(toForm(client));
  const isValid =
    form.name.trim() !== "" &&
    parseAmount(form.payPerUse) !== undefined &&
    parseAmount(form.transportFee) !== undefined;

  return (
    <ConfigurationSection
      title="Relation client"
      description="Repris du CRM Notion : c'est ici qu'il se tient désormais."
    >
      <div className="grid grid-cols-3 gap-4 px-5 py-4">
        <TextField
          label="Nom"
          value={form.name}
          onChange={set("name")}
          maxLength={MAX_LENGTH.name}
        />
        <SelectField
          label="Statut"
          value={form.status}
          options={CLIENT_STATUS_LABELS}
          onChange={set("status")}
        />
        <TextField label="CSM" value={form.csm} onChange={set("csm")} />
        <SelectField
          label="Importance"
          value={form.importance}
          options={CLIENT_IMPORTANCE_LABELS}
          onChange={set("importance")}
        />
        <SelectField
          label="Relation"
          value={form.relationHealth}
          options={CLIENT_RELATION_LABELS}
          onChange={set("relationHealth")}
        />
        <TextField
          label="Agence Shopify"
          value={form.shopifyAgency}
          onChange={set("shopifyAgency")}
        />
        <TextField
          label="Date de signature"
          type="date"
          value={form.signedAt}
          onChange={set("signedAt")}
        />
        <TextField
          label="Pay per use (€ / mois)"
          value={form.payPerUse}
          onChange={set("payPerUse")}
          placeholder="0"
        />
        <TextField
          label="Forfait transport (€ / mois)"
          value={form.transportFee}
          onChange={set("transportFee")}
          placeholder="0"
        />
        <TextField
          label="Compte Front (id)"
          value={form.frontAccountId}
          onChange={set("frontAccountId")}
        />
        <label className="col-span-3 space-y-1">
          <span className="text-xs font-medium text-muted-foreground">
            Notes
          </span>
          <Textarea
            value={form.notes}
            rows={4}
            maxLength={MAX_LENGTH.notes}
            onChange={(event) => set("notes")(event.target.value)}
          />
        </label>
        <div className="col-span-3 flex items-center justify-end gap-3">
          {updateMutation.isError && (
            <span className="text-sm text-destructive">
              L'enregistrement a échoué.
            </span>
          )}
          <Button
            size="sm"
            disabled={!isDirty || !isValid || updateMutation.isPending}
            onClick={() =>
              updateMutation.mutate({
                params: { path: { clientId: client.id } },
                body: changedFields(toBody(form), toBody(toForm(client))),
              })
            }
          >
            {updateMutation.isPending ? "Enregistrement…" : "Enregistrer"}
          </Button>
        </div>
      </div>
    </ConfigurationSection>
  );
}

function ClientShops({ client }: { client: ClientAccountDto }) {
  return (
    <ConfigurationSection
      title={`Boutiques (${client.shops.length})`}
      description="Rattacher une boutique : depuis sa page Configuration, ou en masse depuis la liste des boutiques."
    >
      {client.shops.length === 0 ? (
        <p className="px-5 py-4 text-sm text-muted-foreground">
          Aucune boutique rattachée.
        </p>
      ) : (
        <table className="w-full text-sm">
          <thead className="border-b text-left text-xs text-muted-foreground">
            <tr>
              <th className="px-5 py-2 font-medium">Boutique</th>
              <th className="px-5 py-2 font-medium">Plan</th>
              <th className="px-5 py-2 font-medium">Client Stripe</th>
              <th className="px-5 py-2 text-right font-medium">Abonnement</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {client.shops.map((shop) => (
              <tr key={shop.id}>
                <td className="px-5 py-2">
                  <Link
                    to={`/admin/shops/${shop.id}/configuration`}
                    className="font-medium text-violet-700 hover:underline"
                  >
                    {shop.name}
                  </Link>
                  <div className="text-xs text-muted-foreground">
                    {shop.shopifyUrl}
                  </div>
                </td>
                <td className="px-5 py-2 font-mono text-xs">
                  {shop.planCode ?? "—"}
                </td>
                <td className="px-5 py-2 font-mono text-xs">
                  {shop.stripeCustomerId ?? "—"}
                </td>
                <td className="px-5 py-2 text-right tabular-nums">
                  {shop.monthlyPrice == null
                    ? "—"
                    : formatEuros(shop.monthlyPrice)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot className="border-t">
            <tr>
              <td colSpan={3} className="px-5 py-2 text-right text-xs">
                Total
              </td>
              <td className="px-5 py-2 text-right font-medium tabular-nums">
                {formatEuros(client.monthlyPriceTotal)}
              </td>
            </tr>
          </tfoot>
        </table>
      )}
    </ConfigurationSection>
  );
}

/** Merging and deleting: the two irreversible actions, kept apart. */
function ClientDangerZone({ client }: { client: ClientAccountDto }) {
  const navigate = useNavigate();
  const { data: clients } = useSuperAdminClients();
  const mergeMutation = useMergeSuperAdminClients();
  const deleteMutation = useDeleteSuperAdminClient(client.id);
  const [sourceClientId, setSourceClientId] = useState("");

  const otherClients = (clients ?? []).filter(({ id }) => id !== client.id);
  const source = otherClients.find(({ id }) => id === sourceClientId);

  function handleMerge() {
    if (!source) return;
    const isConfirmed = window.confirm(
      `Absorber « ${source.name} » dans « ${client.name} » ? Ses ${source.shops.length} boutique(s) passent sur ${client.name}, puis « ${source.name} » est supprimé.`,
    );
    if (!isConfirmed) return;
    mergeMutation.mutate(
      {
        params: { path: { clientId: client.id } },
        body: { sourceClientId: source.id },
      },
      { onSuccess: () => setSourceClientId("") },
    );
  }

  function handleDelete() {
    if (!window.confirm(`Supprimer le client « ${client.name} » ?`)) return;
    // A 409 (a shop attached meanwhile) shows below, via `isError`.
    deleteMutation.mutate(
      { params: { path: { clientId: client.id } } },
      { onSuccess: () => navigate("/admin/clients") },
    );
  }

  return (
    <ConfigurationSection
      title="Fusion et suppression"
      description="Deux fiches pour une même entreprise : absorbez l'autre ici. Les valeurs de cette fiche sont gardées, l'autre comble les champs vides ; ses notes et ses valeurs différentes sont recopiées dans les notes, rien n'est perdu."
    >
      <div className="space-y-4 px-5 py-4">
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={sourceClientId}
            onChange={(event) => setSourceClientId(event.target.value)}
            className="h-8 min-w-64 rounded-md border bg-background px-2 text-sm"
          >
            <option value="">Client à absorber…</option>
            {otherClients.map(({ id, name, shops }) => (
              <option key={id} value={id}>
                {name} ({shops.length} boutique{shops.length > 1 ? "s" : ""})
              </option>
            ))}
          </select>
          <Button
            variant="outline"
            size="sm"
            disabled={!source || mergeMutation.isPending}
            onClick={handleMerge}
          >
            Absorber
          </Button>
        </div>
        <div className="flex items-center justify-between border-t pt-4">
          <p className="text-xs text-muted-foreground">
            {client.shops.length > 0
              ? "Suppression impossible tant que des boutiques sont rattachées."
              : "Ce client n'a plus de boutique."}
          </p>
          <Button
            variant="destructive-outline"
            size="sm"
            disabled={client.shops.length > 0 || deleteMutation.isPending}
            onClick={handleDelete}
          >
            Supprimer le client
          </Button>
        </div>
        {(mergeMutation.isError || deleteMutation.isError) && (
          <p className="text-sm text-destructive">L'opération a échoué.</p>
        )}
      </div>
    </ConfigurationSection>
  );
}

export function AdminClientDetailPage() {
  const { clientId = "" } = useParams<{ clientId: string }>();
  const { data: client, isPending, isError } = useSuperAdminClient(clientId);

  return (
    <AdminPageShell
      title={client?.name ?? "Client"}
      description={
        <Link to="/admin/clients" className="hover:underline">
          ← Tous les clients
        </Link>
      }
    >
      {isPending ? (
        <p className="text-sm text-muted-foreground">Chargement…</p>
      ) : isError || !client ? (
        <p className="text-sm text-destructive">Client introuvable.</p>
      ) : (
        <>
          <ClientForm
            key={`${client.id}-${client.updatedAt}`}
            client={client}
          />
          <ClientShops client={client} />
          <ClientDangerZone key={client.id} client={client} />
        </>
      )}
    </AdminPageShell>
  );
}
